import OrderDTO from "@/dto/order/OrderDTO";
import { InjectBaseModalProps } from "@/hooks/Modal/ModalStore";
import { Button, Form, Input, Modal, message } from "antd";
import React, { useMemo, useState } from "react";
import IMEIFormTable, { IMEIFormTableValues } from "./IMEIFormTable";
import { useRequest } from "ahooks";
import {
    UpdateOrderItemExtraInfoOrderDto,
    UpdateOrderItemExtraInfoParams,
    updateOrderItemExtraInfo,
} from "@/services/api/order";
import { isMergeOrder } from "@/utils/OrderUtils";
import { isString, isUndefined, omitBy } from "lodash";
import { useGlobalStore } from "@/store/hooks";
import { showDeviceSnShopTypes, showEanCodeShopTypes, showIMEI2ShopTypes } from "@/utils/ShopUtils";
import { collectEvent } from "@/services/dataTrack";
import DataTrackEvent from "@/services/dataTrack/enum/DataTrackEvent";
import ShopTypeNameMap from "@/map/ShopTypeNameMap";

interface BatchUpdateIMEIModalProps extends InjectBaseModalProps {
    orders: OrderDTO[];
    onRefresh;
    shopId: number;
}

const BatchUpdateIMEIModal: React.FC<BatchUpdateIMEIModalProps> = function (props) {
    const { open, onRefresh, onCancel, orders, shopId } = props;
    const [imeiText, updateImeiText] = useState<string>(undefined);
    const [imei2Text, updateImei2Text] = useState<string>(undefined);
    const [deviceSntext, updateDeviceSntext] = useState<string>(undefined);
    const [eanCodeText, updateEanCodeText] = useState<string>(undefined);
    const [form] = Form.useForm<IMEIFormTableValues>();
    const { runAsync, loading } = useRequest(updateOrderItemExtraInfo, { manual: true });
    const globalStore = useGlobalStore();
    const shop = globalStore.shopMapping.get(shopId);

    const orderMapping = useMemo(() => {
        const mapping = new Map<string, OrderDTO>();

        orders.forEach((order) => {
            if (isMergeOrder(order)) {
                order.childOrders.forEach((childOrder) => {
                    mapping.set(childOrder.originalId, childOrder);
                });
            } else {
                mapping.set(order.originalId, order);
            }
        });

        return mapping;
    }, [orders]);

    function handleSetting() {
        const formValues: IMEIFormTableValues = {};

        function setFormValues(orderOriginalId: string, orderItemOriginalId: string) {
            formValues[orderOriginalId] = formValues[orderOriginalId] || {};
            formValues[orderOriginalId][orderItemOriginalId] = {
                imei: imeiText,
                imei2: imei2Text,
                deviceSn: deviceSntext,
                eanCode: eanCodeText,
            };

            formValues[orderOriginalId][orderItemOriginalId] = omitBy(
                formValues[orderOriginalId][orderItemOriginalId],
                isUndefined,
            );
        }

        orders.forEach((order) => {
            if (isMergeOrder(order)) {
                order.childOrders.forEach((childOrder) => {
                    childOrder.orderItems.forEach((orderItem) => {
                        setFormValues(childOrder.originalId, orderItem.originalId);
                    });
                });
            } else {
                order.orderItems.forEach((orderItem) => {
                    setFormValues(order.originalId, orderItem.originalId);
                });
            }
        });

        form.setFieldsValue(formValues);
    }

    async function handleSave() {
        const values = form.getFieldsValue();
        const params: UpdateOrderItemExtraInfoParams = {
            shopId,
            orders: [],
        };

        Object.keys(values).forEach((orderOriginalId: string) => {
            const order = orderMapping.get(orderOriginalId);
            const orderDto: UpdateOrderItemExtraInfoOrderDto = {
                orderId: order.id,
                mergeSplitStatus: order.mergeSplitStatus,
                orderItems: [],
            };

            Object.keys(values[orderOriginalId]).forEach((orderItemOriginalId: string) => {
                const orderItem = order.orderItems.find((it) => it.originalId === orderItemOriginalId);
                const rowData = values[orderOriginalId]?.[orderItemOriginalId];

                let imeis: string[];
                let imei2s: string[];
                let deviceSns: string[];
                let eanCodes: string[];

                if (rowData && orderItem) {
                    if (isString(rowData.imei) && rowData.imei !== "") {
                        imeis = rowData.imei.trim().split(/[,，]/g);
                    }

                    if (isString(rowData.imei2) && rowData.imei2 !== "") {
                        imei2s = rowData.imei2.trim().split(/[,，]/g);
                    }

                    if (isString(rowData.deviceSn) && rowData.deviceSn !== "") {
                        deviceSns = rowData.deviceSn.trim().split(/[,，]/g);
                    }

                    if (isString(rowData.eanCode) && rowData.eanCode !== "") {
                        eanCodes = rowData.eanCode.trim().split(/[,，]/g);
                    }
                }

                orderDto.orderItems.push({
                    orderItemId: orderItem.id,
                    imeis,
                    imei2s,
                    deviceSns,
                    eanCodes,
                    orderItemOriginalId,
                });
            });

            params.orders.push(orderDto);
        });
        await runAsync(params);

        message.success("设置成功");

        onRefresh();
        onCancel();

        try {
            const shop = globalStore.shopMapping.get(shopId);
            collectEvent(DataTrackEvent.UPDATE_IMEI, {
                shop_id: shop.id,
                shop_name: shop.name,
                shop_type: ShopTypeNameMap[shop.type],
            });
        } catch {}
    }

    return (
        <Modal
            title="录入商品串码(IMEI码)/SN码/69码"
            width={1300}
            open={open}
            onCancel={onCancel}
            okText="保存"
            onOk={handleSave}
            okButtonProps={{ loading }}
        >
            <div className="mb10">
                <Input
                    placeholder="可输入串码相同的数字或字母进行批量设置"
                    style={{ width: 300 }}
                    value={imeiText}
                    onChange={(e) => {
                        updateImeiText(e.target.value);
                    }}
                />

                {showIMEI2ShopTypes.includes(shop?.type) ? (
                    <Input
                        className="ml5"
                        placeholder="输入IMEI2码码相同的数字或字母批量设置"
                        style={{ width: 300 }}
                        value={imei2Text}
                        onChange={(e) => {
                            updateImei2Text(e.target.value);
                        }}
                    />
                ) : null}

                {showDeviceSnShopTypes.includes(shop?.type) ? (
                    <Input
                        className="ml5"
                        placeholder="输入SN码相同的数字或字母批量设置"
                        style={{ width: 300 }}
                        value={deviceSntext}
                        onChange={(e) => {
                            updateDeviceSntext(e.target.value);
                        }}
                    />
                ) : null}

                {showEanCodeShopTypes.includes(shop?.type) ? (
                    <Input
                        className="ml5"
                        placeholder="输入69码相同的数字或字母批量设置"
                        style={{ width: 300 }}
                        value={eanCodeText}
                        onChange={(e) => {
                            updateEanCodeText(e.target.value);
                        }}
                    />
                ) : null}

                <Button type="primary" className="ml10" onClick={handleSetting}>
                    批量设置
                </Button>
            </div>

            <IMEIFormTable orders={orders} form={form} shopType={shop?.type} />
        </Modal>
    );
};

export default React.memo(BatchUpdateIMEIModal);
